/**
 * IterationCard — the structured frame for a chat turn that produced a
 * snapshot. Per the UX/UI report's Differentiator #1 (W3): every chat
 * turn that mutates becomes an inspectable, committable card with inline
 * schematic preview, DRC pulse, BOM delta, and one-click commit.
 *
 * Layout:
 *
 *   ┌─ Iteration · draft 8b7c… ───────────────────── [▣ View ↗] ┐
 *   │  ✎ Added pull-up R(10k) on /UART_RX                       │
 *   │  ✎ Substituted U7 → STM32G031F8P6                         │
 *   │                                                           │
 *   │  ┌──── schematic preview ────┐  ┌─ DRC ──┐  ┌─ BOM ──┐    │
 *   │  │  [CircuitRenderResolver]  │  │ ✓ 0    │  │  +1    │    │
 *   │  └────────────────────────────┘  │ ⚠ 2    │  └────────┘    │
 *   │                                  └────────┘                │
 *   │                                                            │
 *   │  [ Inspect ]  [ Commit as v3.3 ]                           │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Architectural notes:
 *
 *   * Card-level data (snapshot id, ops) comes from `useDetectSnapshotInTurn`
 *     — already extracted by the parent ChatMessage so the card is a
 *     pure prop component.
 *   * IR + DRC are fetched per-snapshot; React Query dedupes with the
 *     DraftPanel and the rack's DraftSegment.
 *   * The CircuitRenderResolver embeds the canvas at a fixed height
 *     (260px) — comfortable preview without dominating the chat scroll.
 */
import { useSnapshot, useSnapshotDrc } from "@features/circuits/hooks/useCircuitIr";
import { CircuitRenderResolver } from "@features/circuits/components/CircuitRenderResolver";
import CommitIcon from "@mui/icons-material/CloudUploadRounded";
import DiffIcon from "@mui/icons-material/CompareArrowsRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import LaunchIcon from "@mui/icons-material/LaunchRounded";
import VerifiedIcon from "@mui/icons-material/VerifiedRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDraftStore } from "@stores/draftStore";
import { type ReactNode, useState } from "react";

import type { DetectedSnapshot } from "../hooks/useDetectSnapshotInTurn";
import { OpSummaryLine } from "./OpSummaryLine";
import { SnapshotDiffDialog } from "./SnapshotDiffDialog";

interface IterationCardProps {
  detection: DetectedSnapshot;
  /**
   * Compact mode for historical messages — collapses the schematic
   * preview + DRC/BOM mini-cards by default and shrinks the action
   * row. The user can click the chevron to expand any individual
   * card. Defaults to `false` so the live / latest assistant turn
   * gets the full treatment.
   */
  compact?: boolean;
}

export function IterationCard({
  detection,
  compact = false,
}: IterationCardProps) {
  const { snapshotId, parentSnapshotId, ops, toolName, committedVersionId } =
    detection;
  const { data: snapshot, isLoading } = useSnapshot(snapshotId);
  const { data: drc } = useSnapshotDrc(snapshotId);
  const { openPanel, activeSnapshotId } = useDraftStore();
  const [expanded, setExpanded] = useState(!compact);
  const [diffOpen, setDiffOpen] = useState(false);

  // The card auto-promotes its snapshot to "active" in the rack on
  // first render, so the user's most recent iteration is what the
  // Instrument Rack carries. Cheap to call — Zustand setter, no
  // network. We only do this once per snapshot id transition.
  const isAlreadyActive = activeSnapshotId === snapshotId;

  const ringTone =
    committedVersionId !== null
      ? "success.main"
      : drc?.has_errors
        ? "error.main"
        : drc && drc.warning_count > 0
          ? "warning.main"
          : "primary.main";

  const isCommit = !!committedVersionId;

  return (
    <Box
      sx={(t) => ({
        mt: 1.25,
        borderRadius: 2,
        // Phosphor-tinted hairline + subtle backdrop. Reads as a sub-card
        // distinct from the chat-bubble background but sharing the
        // surface family.
        bgcolor: t.palette.surface.raised,
        border: `1px solid ${t.palette.border.default}`,
        boxShadow: `inset 0 0 0 1px ${
          isCommit
            ? t.palette.success.main + "1f"
            : t.palette.accent.main + "12"
        }`,
        overflow: "hidden",
      })}
    >
      <CardHeader
        snapshotId={snapshotId}
        toolName={toolName}
        committedVersionId={committedVersionId}
        ringTone={ringTone}
        drc={drc}
        expanded={expanded}
        onToggleExpand={() => setExpanded((e) => !e)}
      />

      {ops.length > 0 && (
        <Box sx={{ px: 1.75, pb: 1, pt: 0.5 }}>
          <Stack spacing={0.25}>
            {ops.slice(0, expanded ? 6 : 2).map((op, i) => (
              // CircuitOp has no stable id; combine kind + index so a
              // reorder on backend rebroadcast doesn't trip React into
              // re-mounting a different op against the wrong DOM node.
              <OpSummaryLine key={`${op.kind}-${i}`} op={op} compact={!expanded} />
            ))}
            {ops.length > (expanded ? 6 : 2) && (
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", pl: 3.5 }}
              >
                +{ops.length - (expanded ? 6 : 2)} more op
                {ops.length - (expanded ? 6 : 2) === 1 ? "" : "s"}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      <Collapse in={expanded} unmountOnExit>
        <Divider sx={{ borderColor: "border.default" }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 220px" },
            gap: 0,
            minHeight: 260,
          }}
        >
          {/* Schematic preview — embeds the resolver inline. Server-SVG
              renders directly; client-layout spins the ELK worker. */}
          <Box
            sx={(t) => ({
              position: "relative",
              borderRight: {
                md: `1px solid ${t.palette.border.default}`,
              },
              borderBottom: {
                xs: `1px solid ${t.palette.border.default}`,
                md: 0,
              },
            })}
          >
            {isLoading && !snapshot && (
              <Box
                sx={{
                  height: 260,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
            {snapshot && (
              <CircuitRenderResolver ir={snapshot.ir} height={260} />
            )}
          </Box>

          {/* Right column — DRC + BOM mini-cards */}
          <Stack
            divider={<Divider sx={{ borderColor: "border.default" }} />}
            sx={{ height: "100%" }}
          >
            <DrcMiniCard
              errCount={drc?.error_count}
              warnCount={drc?.warning_count}
              infoCount={drc?.info_count}
            />
            <BomMiniCard
              componentCount={snapshot?.ir.components.length}
              netCount={snapshot?.ir.nets.length}
            />
          </Stack>
        </Box>
      </Collapse>

      <Divider sx={{ borderColor: "border.default" }} />

      <Stack
        direction="row"
        spacing={1}
        sx={{ p: expanded ? 1.5 : 1, py: expanded ? 1.5 : 0.75 }}
        justifyContent="flex-end"
      >
        {parentSnapshotId && (
          <Tooltip title="Side-by-side compare with the prior snapshot">
            <Button
              size="small"
              variant="text"
              startIcon={<DiffIcon />}
              onClick={() => setDiffOpen(true)}
            >
              Diff vs prior
            </Button>
          </Tooltip>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => openPanel(snapshotId)}
          startIcon={<LaunchIcon />}
        >
          {isAlreadyActive ? "Active in panel" : "Inspect"}
        </Button>
        {!isCommit && (
          <Button
            size="small"
            variant="contained"
            startIcon={<CommitIcon />}
            onClick={() => openPanel(snapshotId)}
          >
            Commit
          </Button>
        )}
      </Stack>

      <SnapshotDiffDialog
        open={diffOpen}
        leftSnapshotId={parentSnapshotId}
        rightSnapshotId={snapshotId}
        onClose={() => setDiffOpen(false)}
      />
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function CardHeader({
  snapshotId,
  toolName,
  committedVersionId,
  ringTone,
  drc,
  expanded,
  onToggleExpand,
}: {
  snapshotId: string;
  toolName: string;
  committedVersionId: string | null;
  ringTone: string;
  drc?: { error_count: number; warning_count: number } | null;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const isCommit = !!committedVersionId;
  const verb =
    toolName === "merge_bom"
      ? "Merged BOM"
      : toolName === "mutate_circuit"
        ? "Edited circuit"
        : toolName === "draft_circuit"
          ? "Drafted circuit"
          : toolName === "render_circuit"
            ? "Rendered circuit"
            : "Iteration";

  // DRC pulse readout for the compact header — small chip showing
  // error/warning counts inline so the user doesn't have to expand to
  // see snapshot health.
  const drcLabel =
    drc == null
      ? null
      : drc.error_count > 0
        ? `${drc.error_count}E`
        : drc.warning_count > 0
          ? `${drc.warning_count}W`
          : "OK";
  const drcColor =
    drc == null
      ? "default"
      : drc.error_count > 0
        ? "error"
        : drc.warning_count > 0
          ? "warning"
          : "success";

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 1.75, py: 1.25 }}
    >
      <Box
        sx={(t) => ({
          width: 8,
          height: 8,
          borderRadius: "50%",
          background:
            t.palette[
              ringTone.split(".")[0] as
                | "primary"
                | "success"
                | "warning"
                | "error"
            ].main,
          boxShadow: `0 0 6px ${
            t.palette[
              ringTone.split(".")[0] as
                | "primary"
                | "success"
                | "warning"
                | "error"
            ].main
          }66`,
          flexShrink: 0,
        })}
      />
      <Typography
        variant="overline"
        sx={{
          color: isCommit ? "success.light" : "text.secondary",
          letterSpacing: "0.12em",
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        {isCommit ? "Committed" : verb}
      </Typography>
      {isCommit && committedVersionId && (
        <Chip
          size="small"
          icon={<VerifiedIcon fontSize="small" />}
          label={`version ${committedVersionId.slice(0, 8)}…`}
          color="success"
          variant="outlined"
          sx={{ fontFamily: "mono", fontSize: 10, height: 18 }}
        />
      )}
      <Box sx={{ flex: 1 }} />
      {drcLabel && (
        <Chip
          size="small"
          label={drcLabel}
          color={drcColor as "default" | "error" | "warning" | "success"}
          variant="outlined"
          sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
        />
      )}
      <Tooltip title={`Snapshot ${snapshotId}`}>
        <Box
          sx={(t) => ({
            fontFamily: t.fonts.mono,
            fontSize: 11,
            color: "text.disabled",
            letterSpacing: "0.04em",
          })}
        >
          {snapshotId.slice(0, 10)}…
        </Box>
      </Tooltip>
      <Tooltip title={expanded ? "Collapse preview" : "Expand preview"}>
        <IconButton
          size="small"
          onClick={onToggleExpand}
          sx={{ p: 0.25, ml: 0.5 }}
        >
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function MiniCard({
  label,
  children,
  tone = "default",
}: {
  label: string;
  children: ReactNode;
  tone?: "default" | "ok" | "warn" | "err";
}) {
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.5,
        borderLeft:
          tone === "err"
            ? `2px solid ${t.palette.error.main}`
            : tone === "warn"
              ? `2px solid ${t.palette.warning.main}`
              : tone === "ok"
                ? `2px solid ${t.palette.success.main}`
                : "2px solid transparent",
      })}
    >
      <Typography
        variant="overline"
        sx={{
          color: "text.disabled",
          fontSize: 10,
          lineHeight: 1,
          letterSpacing: "0.12em",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function DrcMiniCard({
  errCount,
  warnCount,
  infoCount,
}: {
  errCount?: number;
  warnCount?: number;
  infoCount?: number;
}) {
  if (errCount == null) {
    return (
      <MiniCard label="DRC">
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      </MiniCard>
    );
  }
  const tone =
    errCount > 0 ? "err" : warnCount && warnCount > 0 ? "warn" : "ok";
  return (
    <MiniCard label="DRC" tone={tone}>
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={1.25} alignItems="baseline">
          <Box
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 22,
              fontWeight: 600,
              color:
                errCount > 0
                  ? "error.main"
                  : warnCount && warnCount > 0
                    ? "warning.main"
                    : "success.main",
              lineHeight: 1,
            })}
          >
            {errCount + (warnCount ?? 0)}
          </Box>
          <Typography variant="caption" color="text.secondary">
            findings
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {errCount} error{errCount === 1 ? "" : "s"} · {warnCount ?? 0} warning
          {(warnCount ?? 0) === 1 ? "" : "s"}
          {infoCount != null && infoCount > 0 ? ` · ${infoCount} info` : ""}
        </Typography>
      </Stack>
    </MiniCard>
  );
}

function BomMiniCard({
  componentCount,
  netCount,
}: {
  componentCount?: number;
  netCount?: number;
}) {
  if (componentCount == null) {
    return (
      <MiniCard label="BOM">
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      </MiniCard>
    );
  }
  return (
    <MiniCard label="BOM">
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={1.25} alignItems="baseline">
          <Box
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 22,
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1,
            })}
          >
            {componentCount}
          </Box>
          <Typography variant="caption" color="text.secondary">
            comp{componentCount === 1 ? "" : "s"}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {netCount ?? 0} net{netCount === 1 ? "" : "s"}
        </Typography>
      </Stack>
    </MiniCard>
  );
}
